import { useInfiniteQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { ApiError, request } from '@/lib/api'

/**
 * Журнал изменений (тикет #104): вкладка «История» карточки и таблица экрана аудита.
 *
 * Поле без полевого права приходит с сервера помеченным `hidden` и без значений; здесь оно
 * показывается словом «скрыто», а не пустыми ячейками. Пустая ячейка читалась бы как
 * «значения не было», то есть как правда о данных, тогда как это правда о правах.
 *
 * Значение-ссылку (клиент, этап, ответственный) сервер называет (`beforeLabel`/`afterLabel`),
 * а саму запись — `entityLabel`: номер документа, имя контрагента. Названия приходят только
 * тому, кто видит запись; нет названия — показывается ключ, как и раньше.
 */

export interface AuditChange {
  field: string
  before?: unknown
  after?: unknown
  hidden: boolean
  /** Название записи, на которую ссылалось прежнее значение. */
  beforeLabel?: string | null
  /** Название записи, на которую ссылается новое значение. */
  afterLabel?: string | null
}

export interface AuditEntry {
  id: number
  at: string
  actorId?: string | null
  actorName?: string | null
  entityType: string
  entityId?: string | null
  /** Номер документа или имя — только тому, кто вправе читать саму запись. */
  entityLabel?: string | null
  action: string
  changes: AuditChange[]
}

export interface AuditPage {
  entries: AuditEntry[]
  nextCursor?: string | null
}

export const fetchHistory = (entity: string, id: string, cursor: string | null, signal?: AbortSignal) =>
  request<AuditPage>(
    `/entities/${encodeURIComponent(entity)}/${id}/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    { signal },
  )

export function HistoryPanel({ entity, recordId }: { entity: string; recordId: string }) {
  const { t } = useApp()
  const query = useInfiniteQuery({
    queryKey: ['history', entity, recordId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => fetchHistory(entity, recordId, pageParam, signal),
    getNextPageParam: (last) => last.nextCursor ?? null,
  })
  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">{query.error instanceof ApiError ? query.error.message : t('app.error')}</p>
    )
  }
  const entries = (query.data?.pages ?? []).flatMap((page) => page.entries)
  if (entries.length === 0) {
    // The journal exists and is read: an empty answer here is the truth, not a missing API.
    return <p className="muted">{t('history.empty')}</p>
  }
  return (
    <>
      <AuditTable entries={entries} showEntity={false} />
      {query.hasNextPage && (
        <button type="button" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {t('history.more')}
        </button>
      )}
    </>
  )
}

export function AuditTable({ entries, showEntity }: { entries: AuditEntry[]; showEntity: boolean }) {
  const { t, locale } = useApp()
  return (
    <div className="table-scroll">
      <table className="card-table" aria-label={t('history.table')}>
        <thead>
          <tr>
            <th>{t('history.when')}</th>
            <th>{t('history.who')}</th>
            {showEntity && <th>{t('history.entity')}</th>}
            <th>{t('history.action')}</th>
            <th>{t('history.changes')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} data-testid={`audit-${entry.id}`}>
              <td>{new Date(entry.at).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')}</td>
              <td>{entry.actorName ?? t('history.system')}</td>
              {showEntity && (
                <td>
                  {entry.entityLabel ? <span className="strong">{entry.entityLabel} </span> : null}
                  {entry.entityType}
                  {!entry.entityLabel && entry.entityId && <code className="muted"> {entry.entityId}</code>}
                </td>
              )}
              <td>{t(`history.action.${entry.action}`)}</td>
              <td>
                <ul className="plain-list">
                  {entry.changes.map((change) => (
                    <li key={change.field}>
                      <ChangeLine entry={entry} change={change} />
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ChangeLine({ entry, change }: { entry: AuditEntry; change: AuditChange }) {
  const { t } = useApp()
  const key = `column.${change.field}`
  const translated = t(key)
  // Not every journalled property is a list column; its own name beats "column.x".
  const label = translated === key ? change.field : translated
  if (change.hidden) {
    return (
      <span>
        {label}: <em className="muted">{t('history.hidden')}</em>
      </span>
    )
  }
  const before = show(change.before, change.beforeLabel)
  const after = show(change.after, change.afterLabel)
  if (entry.action === 'INSERT') return <span>{label}: {after}</span>
  if (entry.action === 'DELETE') return <span>{label}: {before}</span>
  return (
    <span>
      {label}: {before} → {after}
    </span>
  )
}

/** The server's name for a reference beats its key; a value without a name is shown as is. */
function show(value: unknown, label?: string | null): string {
  if (label) return label
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
